import { create } from 'zustand';

const GLOW_DURATION_MS = 1500;

type PivotGlowState = {
  glowingId: string | null;
  /** Set glowingId for ~1.5s, then auto-clear. Cancels any pending auto-clear. */
  pivotTo: (productId: string) => void;
  /**
   * Cancel any pending auto-clear and null `glowingId`. EditOrder must call
   * this on unmount so a stale glow doesn't leak across orders.
   */
  reset: () => void;
};

let clearTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Holds the most-recently-pivoted product id so rows can render the landing-
 * glow animation. Sharing via a Zustand store avoids prop-drilling through
 * deals-family-view → family-card → family-sku-table.
 *
 * The 1.5s auto-clear timer lives at module scope, which is fine because at
 * most one EditOrder mounts at a time.
 */
export const usePivotGlowStore = create<PivotGlowState>((set) => ({
  glowingId: null,
  pivotTo: (productId) => {
    if (clearTimer) {
      clearTimeout(clearTimer);
    }

    set({ glowingId: productId });

    clearTimer = setTimeout(() => {
      set({ glowingId: null });
      clearTimer = null;
    }, GLOW_DURATION_MS);
  },
  reset: () => {
    if (clearTimer) {
      clearTimeout(clearTimer);
      clearTimer = null;
    }

    set({ glowingId: null });
  },
}));

export function useIsRowGlowing(productId: string): boolean {
  return usePivotGlowStore((state) => state.glowingId === productId);
}
